import { faStar as farStar } from "@fortawesome/pro-regular-svg-icons";
import { faStar as fasStar } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import memoize from "lodash-es/memoize";
import range from "lodash-es/range";
import React from "react";
import { Field, WrappedFieldInputProps, WrappedFieldProps } from "redux-form";

import { FormControl } from "@/mk2/components/forms/FormControl";

import styles from "./StarsRatingField.mscss";

interface StarsRatingInputPublicProps {
    label: string;
    starsCount: number;
}

type StarsRatingInputProps = StarsRatingInputPublicProps & {
    input: WrappedFieldInputProps;
    paddingTop?: boolean;
    transparent?: boolean;
    inToolbar?: boolean;
};

export class StarsRatingInput extends React.Component<StarsRatingInputProps> {
    private handleOnClick = memoize((numStars: number | null) => (ev: React.MouseEvent<any>) => {
        ev.preventDefault();
        ev.stopPropagation();

        const { input } = this.props;
        input.onChange(numStars as any);
    });

    public render() {
        const {
            label,
            input,
            starsCount,
            paddingTop,
            transparent = false,
            inToolbar = false,
        } = this.props;

        return (
            <label
                className={cx(
                    styles.StarsRatingInput,
                    inToolbar && styles["StarsRatingInput--inToolbar"],
                )}
                onClick={this.handleOnClick(null)}
            >
                <div
                    className={cx(
                        styles.StarsRatingInput__text,
                        paddingTop && styles.StarsRatingInput__text__paddingTop,
                        transparent && styles.StarsRatingInput__text__transparent,
                        inToolbar && styles["StarsRatingInput__text--inToolbar"],
                    )}
                >
                    {label}
                </div>

                <div className={styles.StarsRatingInput__stars}>
                    {range(starsCount).map((idx) => (
                        <div
                            className={cx(
                                styles.StarsRatingInput__icon,
                                idx + 1 <= input.value && styles["StarsRatingInput__icon--checked"],
                                paddingTop && styles.StarsRatingInput__icon__paddingTop,
                            )}
                            key={idx + 1}
                            onClick={this.handleOnClick(idx + 1)}
                        >
                            <FontAwesomeIcon icon={idx + 1 <= input.value ? fasStar : farStar} />
                        </div>
                    ))}
                </div>
            </label>
        );
    }
}

interface StarsRatingControlPublicProps {
    marginLeft?: boolean;
    bottomBorder?: boolean;
    paddingRight?: boolean;
    transparent?: boolean;
    paddingTop?: boolean;
    inToolbar?: boolean;
}

type StarsRatingControlProps = StarsRatingInputPublicProps &
    StarsRatingControlPublicProps &
    WrappedFieldProps;

const StarsRatingControl: React.StatelessComponent<StarsRatingControlProps> = ({
    input,
    meta,
    label, // WrappedFieldProps
    transparent = false,
    marginLeft = true,
    paddingRight = false,
    paddingTop = true,
    bottomBorder, // StarsRatingControlPublicProps
    inToolbar = false,
    ...starInputProps // StarsRatingInputPublicProps
}) => (
    <FormControl
        marginLeft={marginLeft}
        bottomBorder={bottomBorder}
        paddingRight={paddingRight} // (ne)ma bocny padding
        paddingTop={paddingTop}
        transparent={transparent}
        input={input}
        label={null /* label sa zobrazi vnutri, nie nad inputom ako je defaultne */}
        meta={meta}
    >
        <StarsRatingInput
            input={input}
            paddingTop={paddingTop}
            transparent={transparent}
            inToolbar={inToolbar}
            label={label || input.name}
            {...starInputProps}
        />
    </FormControl>
);

type StarsRatingFieldProps = StarsRatingInputPublicProps &
    StarsRatingControlPublicProps & {
        // see https://redux-form.com/7.3.0/docs/api/field.md/#props-you-can-pass-to-code-field-code-
        name: string;
    };

export const StarsRatingField: React.StatelessComponent<StarsRatingFieldProps> = (props) => (
    <Field {...props} component={StarsRatingControl} />
);
